import React from 'react';
import moment from 'moment';
import { ORDER_SERVICE_TYPE_LABELS } from '@constants/orderServiceType';
import type { KotLogEntry, KotLogItem } from '@src/types/kotLogging';

export type KotLogGridRow = KotLogEntry & {
  printedByLabel: string;
  serviceLabel: string;
  itemsSummary: string;
  isTotalRow?: boolean;
};

const isTotalRow = (data?: KotLogGridRow) =>
  !!(data?.isTotalRow || data?.id === 'Total:');

const formatItemsSummary = (items: KotLogItem[] | undefined): string => {
  if (!items?.length) return '—';
  return items
    .map((item) => {
      const name = (item.name || '').trim();
      const variant = (item.variant || '').trim();
      const note = (item.note || '').trim();
      const unitPrice = Number(item.unitPrice) || 0;
      const lineTotal =
        typeof item.lineTotal === 'number'
          ? item.lineTotal
          : unitPrice * (Number(item.qty) || 0);
      const base = variant ? `${name} (${variant}) x${item.qty}` : `${name} x${item.qty}`;
      const priced = `${base} @₹${unitPrice} = ₹${lineTotal}`;
      return note ? `${priced} [${note}]` : priced;
    })
    .join('; ');
};

export const toKotLogGridRow = (entry: KotLogEntry): KotLogGridRow => ({
  ...entry,
  printedByLabel: entry.printedBy?.name || entry.printedBy?.phone || entry.printedBy?.uid || '—',
  serviceLabel:
    ORDER_SERVICE_TYPE_LABELS[entry.serviceType] || entry.serviceType || '—',
  itemsSummary: formatItemsSummary(entry.items),
});

const ItemsRenderer = (props: { data?: KotLogGridRow }) => {
  const { data } = props;
  if (!data || isTotalRow(data)) {
    return React.createElement('span', null, '');
  }
  return React.createElement(
    'span',
    {
      title: data.itemsSummary,
      style: {
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
    data.itemsSummary || '—'
  );
};

export const kotLoggingColDefs: any[] = [
  {
    headerName: 'Table',
    field: 'tableNumber',
    width: 120,
    sortable: true,
    suppressMovable: true,
    comparator: (a: string, b: string) =>
      String(a || '').localeCompare(String(b || ''), undefined, {
        numeric: true,
        sensitivity: 'base',
      }),
    valueGetter: (p: any) =>
      isTotalRow(p?.data) ? 'Total' : p?.data?.tableNumber || '—',
  },
  {
    headerName: 'Date / Time',
    field: 'printedAtMs',
    width: 185,
    sortable: true,
    suppressMovable: true,
    comparator: (a: number, b: number) => (Number(a) || 0) - (Number(b) || 0),
    valueFormatter: (p: any) => {
      if (isTotalRow(p?.data)) return '';
      if (!p?.value) return '—';
      return moment(p.value).local().format('DD MMM, h:mm:ss a');
    },
  },
  {
    headerName: 'Group ID',
    field: 'kotSessionId',
    width: 200,
    sortable: true,
    suppressMovable: true,
    tooltipField: 'kotSessionId',
    valueGetter: (p: any) =>
      isTotalRow(p?.data) ? '' : p?.data?.kotSessionId || '—',
  },
  {
    headerName: 'Type',
    field: 'printType',
    width: 110,
    sortable: false,
    suppressMovable: true,
    valueGetter: (p: any) =>
      isTotalRow(p?.data) ? '' : p?.data?.printType || '—',
  },
  {
    headerName: 'Service',
    field: 'serviceLabel',
    width: 110,
    sortable: false,
    suppressMovable: true,
    valueGetter: (p: any) =>
      isTotalRow(p?.data) ? '' : p?.data?.serviceLabel || '—',
  },
  {
    headerName: 'Qty',
    field: 'totalQty',
    width: 90,
    sortable: true,
    suppressMovable: true,
    type: 'numericColumn',
  },
  {
    headerName: 'Items Cost',
    field: 'totalItemsCost',
    width: 120,
    sortable: true,
    suppressMovable: true,
    type: 'numericColumn',
    valueGetter: (p: any) => {
      if (isTotalRow(p?.data)) return p?.data?.totalItemsCost ?? 0;
      const cost = Number(p?.data?.totalItemsCost);
      return Number.isFinite(cost) ? cost : 0;
    },
    valueFormatter: (p: any) => {
      const cost = Number(p?.value);
      if (!Number.isFinite(cost)) return '—';
      return `₹${cost.toFixed(2)}`;
    },
  },
  {
    headerName: 'Lines',
    field: 'itemCount',
    width: 90,
    sortable: true,
    suppressMovable: true,
    type: 'numericColumn',
  },
  {
    headerName: 'Printed by',
    field: 'printedByLabel',
    width: 150,
    sortable: true,
    suppressMovable: true,
    valueGetter: (p: any) =>
      isTotalRow(p?.data) ? '' : p?.data?.printedByLabel || '—',
  },
  {
    headerName: 'Channel',
    field: 'printerChannel',
    width: 110,
    sortable: false,
    suppressMovable: true,
    valueGetter: (p: any) =>
      isTotalRow(p?.data) ? '' : p?.data?.printerChannel || '—',
  },
  {
    headerName: 'Source',
    field: 'source',
    width: 120,
    sortable: false,
    suppressMovable: true,
    valueGetter: (p: any) =>
      isTotalRow(p?.data) ? '' : p?.data?.source || '—',
  },
  {
    headerName: 'Items',
    field: 'itemsSummary',
    flex: 1,
    minWidth: 220,
    sortable: false,
    suppressMovable: true,
    cellRenderer: ItemsRenderer,
  },
];
